import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { matchesSearchTerms } from "@/lib/searchUtils";

export interface SearchableSelectItem {
  value: string | number;
  label: string;
  node?: React.ReactNode;
  group?: string; // Optional group for grouped items
  data?: any; // Optional data payload for onItemSelect callback
}

// Base props shared by both modes
interface BaseSearchableSelectProps {
  items: SearchableSelectItem[];
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  loading?: boolean;
  /** Optional callback when an item is selected, receives the full item */
  onItemSelect?: (item: SearchableSelectItem | null) => void;
  /** Optional callback when search input changes - useful for async/remote search */
  onSearchChange?: (search: string) => void;
  /** 
   * Fallback label to show when value is set but not found in items.
   * Use this when items are filtered (e.g. by hierarchy/level) but you still 
   * want to display the currently saved value even if it's not in the list.
   */
  fallbackLabel?: string;
}

// Single select mode props
interface SingleSelectProps extends BaseSearchableSelectProps {
  mode?: "single";
  value?: string | number;
  onValueChange: (value: string) => void;
}

// Multi select mode props
interface MultiSelectProps extends BaseSearchableSelectProps {
  mode: "multi";
  value: string[];
  onValueChange: (value: string[]) => void;
}

export type SearchableSelectProps = SingleSelectProps | MultiSelectProps;

export const SearchableSelect = React.forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (props, ref) => {
    const {
      items,
      placeholder = "Auswählen...",
      emptyText = "Nichts gefunden.",
      searchPlaceholder = "Suchen...",
      className,
      disabled = false,
      allowClear = false,
      loading = false,
      onItemSelect,
      onSearchChange,
      fallbackLabel,
    } = props;

    const isMulti = props.mode === "multi";
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Auto-focus input when popover opens
    React.useEffect(() => {
      if (open) {
        // Small delay to ensure popover is mounted
        const timer = setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [open]);

    // Get current value(s)
    const singleValue = !isMulti ? String(props.value ?? "") : "";
    const multiValue = isMulti ? props.value : [];

    // Determine loading state
    const isLoading = loading || (items.length === 0 && !isMulti && singleValue !== "");

    // Find selected item(s)
    const selectedItem = !isMulti ? items.find(item => String(item.value) === singleValue) : null;
    const selectedItems = isMulti ? items.filter(item => multiValue.includes(String(item.value))) : [];

    // Group items by group property
    const groupedItems = React.useMemo(() => {
      const groups: { [key: string]: SearchableSelectItem[] } = {};
      const ungrouped: SearchableSelectItem[] = [];
      
      items.forEach(item => {
        if (item.group) {
          if (!groups[item.group]) {
            groups[item.group] = [];
          }
          groups[item.group].push(item);
        } else {
          ungrouped.push(item);
        }
      });
      
      return { groups, ungrouped };
    }, [items]);

    const hasGroups = Object.keys(groupedItems.groups).length > 0;

    // Display value
    const getDisplayValue = () => {
      if (isLoading) {
        return <Skeleton className="h-4 w-24 inline-block" />;
      }
      
      if (isMulti) {
        if (selectedItems.length === 0) return placeholder;
        if (selectedItems.length === 1) return selectedItems[0].node || selectedItems[0].label;
        return `${selectedItems.length} ausgewählt`;
      }
      
      // Single mode: show selected item, fallback label, or placeholder
      if (selectedItem) {
        return selectedItem.node || selectedItem.label;
      }
      
      // Value is set but item not found in list - show fallback if provided
      if (singleValue && fallbackLabel) {
        return <span className="text-muted-foreground">{fallbackLabel}</span>;
      }
      
      return placeholder;
    };

    // Handle single select
    const handleSingleSelect = (itemValue: string, item?: SearchableSelectItem) => {
      if (!isMulti) {
        (props as SingleSelectProps).onValueChange(itemValue);
        if (onItemSelect) {
          onItemSelect(item || null);
        }
        setOpen(false);
        setSearchValue("");
      }
    };

    // Handle multi select
    const handleMultiSelect = (itemValue: string, item?: SearchableSelectItem) => {
      if (isMulti) {
        const currentValue = (props as MultiSelectProps).value;
        const isSelected = currentValue.includes(itemValue);
        const newValue = isSelected
          ? currentValue.filter(v => v !== itemValue)
          : [...currentValue, itemValue];
        (props as MultiSelectProps).onValueChange(newValue);
        
        if (onItemSelect && !isSelected) {
          onItemSelect(item || null);
        }
      }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isMulti) {
        (props as MultiSelectProps).onValueChange([]);
      } else {
        (props as SingleSelectProps).onValueChange("");
        if (onItemSelect) {
          onItemSelect(null);
        }
      }
      setSearchValue("");
    };

    // Filter function for search
    const filterFunction = React.useCallback((itemValue: string, search: string) => {
      if (itemValue === "__clear__") return 1;
      const item = items.find(i => `${i.label} ${i.value}` === itemValue);
      if (!item) return 0;
      return matchesSearchTerms(search, item.label, String(item.value)) ? 1 : 0;
    }, [items]);

    // Filter items for multi mode or grouped mode (manual filtering)
    const filteredItems = React.useMemo(() => {
      if (!searchValue.trim()) return items;
      return items.filter(item => 
        matchesSearchTerms(searchValue, item.label, String(item.value))
      );
    }, [items, searchValue]);

    // Filter grouped items
    const filteredGroupedItems = React.useMemo(() => {
      if (!searchValue.trim()) return groupedItems;
      
      const groups: { [key: string]: SearchableSelectItem[] } = {};
      const ungrouped: SearchableSelectItem[] = [];
      
      filteredItems.forEach(item => {
        if (item.group) {
          if (!groups[item.group]) {
            groups[item.group] = [];
          }
          groups[item.group].push(item);
        } else {
          ungrouped.push(item);
        }
      });
      
      return { groups, ungrouped };
    }, [filteredItems, groupedItems, searchValue]);

    const showClearButton = allowClear && (isMulti ? multiValue.length > 0 : singleValue !== "");

    const renderItem = (item: SearchableSelectItem) => {
      const itemValueStr = String(item.value);
      const isSelected = isMulti 
        ? multiValue.includes(itemValueStr)
        : singleValue === itemValueStr;

      return (
        <CommandItem
          key={item.value}
          value={`${item.label} ${item.value}`}
          onSelect={() => isMulti ? handleMultiSelect(itemValueStr, item) : handleSingleSelect(itemValueStr, item)}
          className="cursor-pointer"
        >
          {isMulti ? (
            <div className="flex items-center gap-2 w-full">
              <div className={cn(
                "flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0",
                isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
              )}>
                {isSelected && <Check className="h-3 w-3" />}
              </div>
              {item.node || <span className="flex-1 truncate">{item.label}</span>}
            </div>
          ) : (
            <>
              <Check className={cn("mr-2 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
              {item.node || item.label}
            </>
          )}
        </CommandItem>
      );
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full min-w-[80px] justify-between cursor-pointer", className)}
            disabled={disabled}
          >
            <span className="truncate flex-1 text-left min-w-0">
              {getDisplayValue()}
            </span>
            <div className="flex items-center gap-1 ml-2">
              {showClearButton && (
                <X
                  className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className={cn(
            "w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[400px] p-0 bg-popover border border-border shadow-md z-[9999]",
            // Disable exit animation to prevent flicker/teleportation when closing
            "data-[state=closed]:duration-0 data-[state=closed]:animate-none"
          )}
          align="start"
          sideOffset={4}
          avoidCollisions
          collisionPadding={16}
        >
          <Command
            className="overflow-hidden"
            filter={hasGroups || isMulti ? undefined : filterFunction}
            shouldFilter={!hasGroups && !isMulti}
          >
            <CommandInput
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={(val) => {
                setSearchValue(val);
                onSearchChange?.(val);
              }}
            />
            <CommandList 
              className="max-h-[250px] overflow-y-auto overflow-x-hidden scrollbar-thin"
              onWheel={(e) => e.stopPropagation()}
            >
              <CommandEmpty>{emptyText}</CommandEmpty>
              
              {/* Clear option for single mode */}
              {!isMulti && allowClear && (
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    onSelect={() => handleSingleSelect("")}
                    className="cursor-pointer"
                  >
                    <Check className={cn("mr-2 h-4 w-4", singleValue === "" ? "opacity-100" : "opacity-0")} />
                    <span className="text-muted-foreground italic">Keine Auswahl</span>
                  </CommandItem>
                </CommandGroup>
              )}

              {/* Render grouped items */}
              {hasGroups ? (
                <>
                  {/* Ungrouped items first */}
                  {filteredGroupedItems.ungrouped.length > 0 && (
                    <CommandGroup>
                      {filteredGroupedItems.ungrouped.map(renderItem)}
                    </CommandGroup>
                  )}
                  
                  {/* Grouped items */}
                  {Object.entries(filteredGroupedItems.groups).map(([groupName, groupItems]) => (
                    <CommandGroup key={groupName} heading={groupName}>
                      {groupItems.map(renderItem)}
                    </CommandGroup>
                  ))}
                </>
              ) : (
                /* Render flat items */
                <CommandGroup>
                  {(isMulti ? filteredItems : items).map(renderItem)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";

// Backward compatibility export for MultiSearchableSelect
export type MultiSearchableSelectItem = SearchableSelectItem;
export type MultiSearchableSelectProps = Omit<MultiSelectProps, 'mode'>;

export const MultiSearchableSelect = React.forwardRef<HTMLButtonElement, MultiSearchableSelectProps>(
  (props, ref) => {
    return <SearchableSelect ref={ref} mode="multi" {...props} />;
  }
);

MultiSearchableSelect.displayName = "MultiSearchableSelect";